import {
  type ObjectOptions,
  type TProperties,
  type TSchema,
  type TUnsafe,
  Type as t,
} from "@sinclair/typebox";

/** A string-literal union whose static type is the exact union of the given values. */
export function literals<const V extends readonly string[]>(
  values: V,
  options?: { description?: string },
): TUnsafe<V[number]> {
  return t.Unsafe<V[number]>(
    t.Union(
      values.map((v) => t.Literal(v)),
      options,
    ),
  );
}

/**
 * An object that forbids every property it does not declare. Every object of a public response is built
 * with this (U-15b): the API kit validates a response before sending it, so a field the contract does not
 * name makes the request fail with a 500 instead of travelling to the browser. A guard test walks every
 * public response schema and fails on an object that is not closed.
 */
export function closed<P extends TProperties>(properties: P, options: ObjectOptions = {}) {
  return t.Object(properties, { ...options, additionalProperties: false });
}

/** `T | null` on the wire. */
export function nullable<T extends TSchema>(schema: T) {
  return t.Union([schema, t.Null()]);
}

/**
 * Query and path parameters arrive as strings. These helpers describe the string that is validated
 * (a pattern) and tag it with `x-decode`; the static type is already the decoded one (number, boolean,
 * string[]), so handlers and the web client see plain types. `decodeParams`/`encodeParams` in
 * contract/codec.ts do the conversion at the edges.
 */
export type ParamKind = "int" | "float" | "bool" | "list";
export const PARAM_KEY = "x-decode";

/** digits on the wire, number in handlers */
export function intParam(pattern = "^[0-9]{1,15}$", description?: string) {
  return t.Unsafe<number>(t.String({ pattern, [PARAM_KEY]: "int", ...(description ? { description } : {}) }));
}

/** a decimal number on the wire, number in handlers */
export function floatParam(pattern: string, description?: string) {
  return t.Unsafe<number>(
    t.String({ pattern, [PARAM_KEY]: "float", ...(description ? { description } : {}) }),
  );
}

/** "true" | "false" on the wire, boolean in handlers */
export function boolParam(description?: string) {
  return t.Unsafe<boolean>(
    t.Union([t.Literal("true"), t.Literal("false")], {
      [PARAM_KEY]: "bool",
      ...(description ? { description } : {}),
    }),
  );
}

/** `?k=a&k=b` (array) or `?k=a` (single string) on the wire, always a string[] in handlers */
export function listParam(description?: string) {
  return t.Unsafe<string[]>(
    t.Union([t.String({ minLength: 1 }), t.Array(t.String({ minLength: 1 }), { maxItems: 50 })], {
      [PARAM_KEY]: "list",
      ...(description ? { description } : {}),
    }),
  );
}
